// Modal to view/set monthly recurring revenue targets.
// Managers/admins only. Rep-scoped or team-wide, by kind (total/acq/growth).
import { useEffect, useState } from "react";
import { X, Trash2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import PragmaSelect from "./PragmaSelect";

const KINDS = [
  { k: "total", label: "Total" },
  { k: "acquisition", label: "Acquisition" },
  { k: "growth", label: "Growth" },
];

export default function AddTargetModal({ users, initialKind = "total", onClose, onSaved }) {
  const [scope, setScope] = useState("team");
  const [userId, setUserId] = useState(users[0]?.id || "");
  const [kind, setKind] = useState(initialKind);
  const [amount, setAmount] = useState("");
  const [targets, setTargets] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get("/targets"); setTargets(r.data); }
    catch { toast.error("Failed to load targets"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter a positive amount"); return; }
    setSaving(true);
    try {
      await api.post("/targets", {
        scope,
        user_id: scope === "rep" ? userId : null,
        kind,
        monthly_amount: Number(amount),
      });
      toast.success("Target saved");
      setAmount("");
      await load();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save target");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/targets/${id}`); await load(); onSaved && onSaved(); }
    catch { toast.error("Failed to delete"); }
  };

  const userName = (uid) => (users.find(u => u.id === uid)?.name) || "—";

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} data-testid="target-modal-backdrop" />
      <div className="side-panel" data-variant="center" data-testid="target-modal"
           style={{ maxWidth: 560 }}>
        <div className="px-6 py-5 flex justify-between items-start"
             style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <div>
            <h2 className="text-xl">Monthly Revenue Targets</h2>
            <div className="mono text-[11px] text-mute mt-1">
              Recurring — applies to every month until changed
            </div>
          </div>
          <button onClick={onClose} className="pg-btn-icon"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Add form */}
          <div className="space-y-3" data-testid="target-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Scope</label>
                <PragmaSelect
                  value={scope}
                  onChange={setScope}
                  options={[
                    { value: "team", label: "Team (all reps combined)" },
                    { value: "rep",  label: "Individual rep" },
                  ]}
                  testId="target-scope"
                />
              </div>
              <div>
                <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Kind</label>
                <PragmaSelect
                  value={kind}
                  onChange={setKind}
                  options={KINDS.map((k) => ({ value: k.k, label: k.label }))}
                  testId="target-kind"
                />
              </div>
            </div>
            {scope === "rep" && (
              <div>
                <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Rep</label>
                <PragmaSelect
                  value={userId}
                  onChange={setUserId}
                  options={users.map((u) => ({ value: u.id, label: u.name, hint: u.job_title || u.email }))}
                  testId="target-user"
                />
              </div>
            )}
            <div>
              <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">
                Monthly Amount
              </label>
              <input
                className="pg-input"
                type="number" min="0" step="1000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 50000"
                data-testid="target-amount"
              />
            </div>
          </div>

          {/* Existing */}
          {targets.length > 0 && (
            <div>
              <div className="mono text-[11px] text-mute mb-2 uppercase tracking-wider">
                Existing targets
              </div>
              <div className="space-y-2">
                {targets.map(t => (
                  <div key={t.id}
                       className="flex items-center justify-between px-3 py-2 rounded-lg"
                       style={{ border: "1px solid var(--pg-border)", background: "var(--pg-bg)" }}
                       data-testid={`target-row-${t.id}`}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--pg-text)" }}>
                        {t.scope === "team" ? "Team" : userName(t.user_id)}
                        <span className="mono text-[11px] text-mute" style={{ marginLeft: 8, fontWeight: 500 }}>
                          · {t.kind}
                        </span>
                      </div>
                      <div className="mono text-[11px] text-mute" style={{ fontFeatureSettings: '"tnum" 1' }}>
                        ${Number(t.monthly_amount).toLocaleString()}/mo
                      </div>
                    </div>
                    <button onClick={() => del(t.id)} className="pg-btn-icon"
                            data-testid={`target-delete-${t.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 flex justify-end gap-2"
             style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button className="pg-btn" onClick={onClose}>Close</button>
          <button className="pg-btn pg-btn-primary" onClick={save} disabled={saving}
                  data-testid="target-save">
            {saving ? "Saving…" : "Save target"}
          </button>
        </div>
      </div>
    </>
  );
}
